import type { FieldSet, Record } from '@rogoag/airtable/crm';
import { crmCrud } from '../airtable_crm';
import { LocalAtRecord, LocalAtSearch, SelectedRecord } from '../db';
import { cleanSet } from '../utils';

export async function refresh<T extends FieldSet = FieldSet>(
  this: LocalAtSearch<T>,
  { waitForAttachments = true } = {},
) {
  //console.log(`!!! airtable_search_ops.refresh(${waitForAttachments})`);
  if (!this.currentRefresh) {
    this.currentRefresh = Promise.resolve();
  }

  this.currentRefresh = new Promise<void>(async (res, rej) => {
    // don't allow starting one sync before other sync finishes
    try {
      await this.currentRefresh;
    } catch (e) {
      console.error('Error in previous refresh', e);
    }

    try {
      const rs: Record<FieldSet>[] = await crmCrud.select(this.table, this.view, this.query);

      // for each record that no longer matches the AT search, remove from local search
      const rIds = rs.map((r) => r.id);

      for (const sr of cleanSet(this.getSelectedRecords())) {
        if (!rIds.includes(sr.record_id!)) {
          sr.dispose();
        }
      }

      // for each record from airtable, create or update a record
      for (const r of rs) {
        let record = cleanSet(this.getSelectedRecords().map((sr) => sr.get_AirtableRecord())).find(
          (sel) => sel?.id === r.id,
        );

        if (!record) {
          // see if the record exists in a different search
          record = LocalAtRecord.findOne<LocalAtRecord<T>>(
            (sel) => !!sel && sel.table === this.table && sel.id === r.id,
          );

          if (!record) {
            // create new record
            record = LocalAtRecord.create();
            record.table = this.table;
            record.id = r.id;
            record.modified_fields = new Set<string>();
          }

          const selected_record = SelectedRecord.create();
          selected_record.AirtableRecord_id = record.instance_id;
          selected_record.AirtableSearch_id = this.instance_id;
        }

        // if ((record.fields['Name'] as string).includes("Newdale")) {
        //   console.log("I care about this one");
        // }

        // if a record isn't dirty, update it with the remote fields
        if (!record.dirty) {
          // @ts-ignore FieldSet is not assignable to generic T
          record.fields = r.fields;

          // if a record is dirty, check if the modified fields conflict
          // if they don't, then update with the remote fields
        } else {
          // compared locally modified fields to remote fields
          // if they are the same, clear the dirty flag
          // if they are different, do nothing
          let different = false;

          for (const f of record.modified_fields) {
            // @ts-ignore keyof T cannot index FieldSet
            if (record.fields[f] !== r.fields[f]) {
              console.log(`skipping dirty record, instance_id: ${record._instance_id}`);
              different = true;
              break;
            }
          }

          if (different) {
            continue;
          }

          record.dirty = false;
          // @ts-ignore FieldSet is not assignable to generic T
          record.fields = r.fields;
        }

        if (waitForAttachments) {
          await record.refresh_attachments();
        }
      }
    } catch (err) {
      console.log(err);
      rej(err);
    }

    res();
  });

  return this.currentRefresh;
}

export function getRecords<T extends FieldSet = FieldSet>(this: LocalAtSearch<T>) {
  return cleanSet(this.getSelectedRecords().map((sr: SelectedRecord) => sr.get_AirtableRecord())).filter(
    (sel) => !!sel,
  );
}

export async function getLocalAtSearch<T extends FieldSet = FieldSet>(
  tableName: string,
  viewName = '',
  query = '',
  refreshOnCreate = true,
): Promise<LocalAtSearch<T>> {
  // Search Airtable and cache the records and attachements
  // return an array of records found

  // find existing instance of this search. if none exists, create it
  let search: LocalAtSearch | undefined = LocalAtSearch.findOne(
    (sel) => !!sel && sel.table === tableName && sel.view === viewName && sel.query === query,
  );

  if (!search) {
    // create a new search
    search = LocalAtSearch.create();
    search.table = tableName;
    search.view = viewName;
    search.query = query;

    // refresh the search
    if (refreshOnCreate) {
      await search.refresh();
    }
  }

  // We created a search if it didn't exist
  // => this method will not return undefined (can add ! at the end)
  return LocalAtSearch.findOne<LocalAtSearch<T>>(
    (sel) => !!sel && sel.table === tableName && sel.view === viewName && sel.query === query,
  )!;
}
